import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

function ResetPassword() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordMismatch') || 'Пароли не совпадают');
      return;
    }
    
    if (password.length < 8 || !/[A-Za-zА-Яа-я]/.test(password) || !/\d/.test(password)) {
      toast.error('Пароль должен быть не короче 8 символов и содержать букву и цифру');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success(t('auth.passwordChanged') || 'Пароль успешно изменён');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.resetError') || 'Ошибка сброса пароля');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm border p-8">
            {!token || !valid ? (
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('auth.invalidLink') || 'Недействительная ссылка'}
                </h2>
                <p className="text-gray-500 mb-6">
                  {t('auth.linkExpiredOrInvalid') || 'Ссылка для сброса пароля истекла или недействительна'}
                </p>
                <Link to={`/${currentLang}/forgot-password`}>
                  <Button className="w-full bg-emerald-700 hover:bg-emerald-800">
                    {t('auth.requestNewLink') || 'Запросить новую ссылку'}
                  </Button>
                </Link>
              </div>
            ) : success ? (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('auth.passwordChangedTitle') || 'Пароль изменён'}
                </h2>
                <p className="text-gray-500 mb-6">
                  {t('auth.passwordChangedDesc') || 'Теперь вы можете войти с новым паролем'}
                </p>
                <Link to={`/${currentLang}/login`}>
                  <Button className="w-full bg-emerald-700 hover:bg-emerald-800">
                    {t('auth.goToLogin') || 'Перейти к входу'}
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <Lock className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('auth.newPasswordTitle') || 'Новый пароль'}
                  </h1>
                  <p className="text-gray-500">
                    {t('auth.newPasswordFor') || 'Создайте новый пароль для'}{' '}
                    <span className="font-medium">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="password" className="text-gray-700">
                      {t('auth.newPassword') || 'Новый пароль'}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="mt-1"
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-gray-700">
                      {t('auth.confirmPassword') || 'Подтвердите пароль'}
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="mt-1"
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                    data-testid="reset-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('auth.saving') || 'Сохранение...'}
                      </>
                    ) : (
                      t('auth.saveNewPassword') || 'Сохранить пароль'
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default ResetPassword;
